import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../app/api";

interface AnalyticsData {
  start_date: string;
  end_date: string;
  waivers: Record<string, any>[];
  participants: Record<string, any>[];
}

export default function useAnalytics() {
  const params = useParams<{ tab: "waivers" | "participants" }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [dataOptions, setDataOptions] = useState<null | Record<string, any>>(
    null
  );
  const [granularity, setGranularity] = useState<
    "daily" | "weekly" | "monthly"
  >("daily");
  const [dateRange, setDateRange] = useState<{ from?: string; to?: string }>(
    {}
  );
  const [waiverIds, setWaiverIds] = useState<number[]>([]);
  const [locationIds, setLocationIds] = useState<number[]>([]);

  const initDateRange = (granularity: "daily" | "weekly" | "monthly") => {
    const now = new Date();

    if ("monthly" === granularity) {
      setDateRange({
        from: new Date(now.getFullYear(), 0, 1).toISOString().split("T")[0],
        to: new Date(now.getFullYear(), 11, 31).toISOString().split("T")[0],
      });
    }

    if ("weekly" === granularity) {
      setDateRange({
        from: new Date(now.getFullYear(), now.getMonth(), 1)
          .toISOString()
          .split("T")[0],
        to: new Date(now.getFullYear(), now.getMonth() + 1, 0)
          .toISOString()
          .split("T")[0],
      });
    }

    if ("daily" === granularity) {
      const monday = new Date(now);
      const sunday = new Date(monday);

      monday.setDate(
        now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1)
      );
      sunday.setDate(monday.getDate() + 6);

      setDateRange({
        from: monday.toISOString().split("T")[0],
        to: sunday.toISOString().split("T")[0],
      });
    }
  };

  const fetchOptions = async () => {
    const response = await api.get("/waivers/options?component=analytics");

    if (response.success && response.data.data) {
      const options = response.data.data.analytics;

      if (!options?.columns || !Object.keys(options.columns).length) {
        setDataOptions({});
        return;
      }

      const data: Record<string, any> = {};

      Object.keys(options.columns).forEach(key => {
        const column = options.columns[key];
        const filter = column.filter;

        data[key] = filter;

        if (filter.type === "dropdown" && filter.data) {
          data[key].data = filter.data.map((item: Record<string, any>) => ({
            ...item,
            value: item.id,
          }));
        }
      });

      setDataOptions(data);
    }
  };

  const fetchData = async () => {
    setLoading(true);

    const params = {
      granularity,
      ...(dateRange.from && { start_date: dateRange.from }),
      ...(dateRange.to && { end_date: dateRange.to }),
      waiver_ids: waiverIds,
      location_ids: locationIds,
    };

    const response = await api.get("/waivers/analytics", { params });

    if (response.success && response.data.data) {
      setData(response.data.data);

      setDateRange({
        from: response.data.data.start_date,
        to: response.data.data.end_date,
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!dataOptions) {
      fetchOptions();

      return;
    }

    if (!dateRange.from || !dateRange.to) {
      initDateRange(granularity);
    } else {
      fetchData();
    }
  }, [
    granularity,
    dateRange.from,
    dateRange.to,
    waiverIds,
    locationIds,
    dataOptions,
    params.tab,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const getChartData = () => {
    if (!data) {
      return [];
    }

    if (params.tab === "participants") {
      return data.participants.map(item => ({
        ...item,
        count: Number(item.count),
      }));
    }

    return data.waivers.map(item => ({
      ...item,
      count: Number(item.count),
    }));
  };

  const handleGranularityChange = (e: any) => {
    setGranularity(e.target.value);
    initDateRange(e.target.value);
  };

  const handleWaiversChange = (e: any) => {
    setWaiverIds(e.target.value);
  };

  const handleLocationChange = (e: any) => {
    setLocationIds(e.target.value);
  };

  const handleDateRangeChange = (value: any) => {
    setDateRange(value);
  };

  return {
    loading,
    granularity,
    dateRange,
    activeTab: params.tab || "waivers",
    dataOptions,
    waiverIds,
    locationIds,
    getChartData,
    handleGranularityChange,
    handleWaiversChange,
    handleLocationChange,
    handleDateRangeChange,
  };
}
